/**
 * FAQ Accordion Component
 * Displays a list of FAQs in an expandable accordion format
 * Following Shopify Polaris design guidelines
 */
import { formatTextWithLineBreaks, translateMe } from '@/helpers/helper.js';

class FaqAccordion extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this._faqs = [];
        this._expandedItems = new Set();
    }

    get faqs() { return this._faqs; }
    set faqs(value) {
        this._faqs = Array.isArray(value) ? value : [];
        this._render();
    }

    connectedCallback() {
        this._render();
    }

    _render() {
        if (!this.shadowRoot) return;

        const visibleFaqs = this._faqs.slice(0, 6);
        // Keep expanded state only for currently visible FAQ indices.
        this._expandedItems = new Set([...this._expandedItems].filter(index => index < visibleFaqs.length));

        const faqsHTML = visibleFaqs
            .map((faq, index) => this._renderFaqItem(faq, index))
            .join('');

        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    font-family: -apple-system, BlinkMacSystemFont, San Francisco, Roboto, Segoe UI, Helvetica Neue, sans-serif;
                }

                .faq-accordion {
                    width: 100%;
                    border: 1px solid #e5e5e5;
                    border-radius: 12px;
                    overflow: hidden;
                    background-color: #ffffff;
                    box-shadow: 0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px 0 rgba(0,0,0,0.04);
                }

                .faq-accordion__item {
                    border: none;
                    border-bottom: 1px solid #e5e5e5;
                }

                .faq-accordion__item:last-child {
                    border-bottom: none;
                }

                .faq-accordion__header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 12px;
                    background-color: #ffffff;
                    cursor: pointer;
                    border: none;
                    width: 100%;
                    text-align: left;
                    transition: background-color 0.15s ease;
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 600;
                    color: #303030;
                }

                .faq-accordion__header:hover,
                .faq-accordion__header--expanded {
                    background-color: #fafafa;
                }

                .faq-accordion__header-text {
                    flex: 1;
                    margin: 0;
                    padding: 0;
                    text-align: left;
                    font-weight: 600;
                    color: #303030;
                    font-size: 14px;
                }

                .faq-accordion__icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 20px;
                    height: 20px;
                    margin-left: 16px;
                    flex-shrink: 0;
                    color: #616161;
                    transition: transform 0.25s ease;
                }

                .faq-accordion__icon--expanded {
                    transform: rotate(180deg);
                }

                .faq-accordion__content {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease, opacity 0.3s ease;
                    opacity: 0;
                }

                .faq-accordion__content--expanded {
                    max-height: 500px;
                    opacity: 1;
                }

                .faq-accordion__body {
                    padding: 8px 12px 16px 12px;
                    background-color: #ffffff;
                    font-size: 14px;
                    line-height: 20px;
                    color: #616161;
                }

                .faq-accordion__body p {
                    margin: 0;
                    padding: 0;
                }

                .faq-accordion__body p:not(:last-child) {
                    margin-bottom: 12px;
                }

                .faq-accordion__body {
                    white-space: pre-wrap;
                    word-break: break-word;
                }

                @media screen and (max-width: 768px) {
                    .faq-accordion__body,
                    .faq-accordion__header-text,
                    .faq-accordion__header {
                        font-size: 13px;
                    }
                }
            </style>

            <div class="faq-accordion">
                ${faqsHTML}
            </div>
        `;

        this._attachEventListeners();
    }

    _renderFaqItem(faq, index) {
        const isExpanded = this._expandedItems.has(index);
        return `
            <div class="faq-accordion__item" data-faq-index="${index}">
                <button 
                    class="faq-accordion__header ${isExpanded ? 'faq-accordion__header--expanded' : ''}" 
                    aria-expanded="${isExpanded}" 
                    aria-controls="faq-content-${index}"
                    data-faq-trigger="${index}"
                >
                    <span class="faq-accordion__header-text">${this._escapeHTML(translateMe(faq.question))}</span>
                    <span class="faq-accordion__icon ${isExpanded ? 'faq-accordion__icon--expanded' : ''}">
                        <svg viewBox="0 0 20 20" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </button>
                <div
                    class="faq-accordion__content ${isExpanded ? 'faq-accordion__content--expanded' : ''}"
                    id="faq-content-${index}"
                >
                    <div class="faq-accordion__body">${this._renderAnswer(faq.answer)}</div>
                </div>
            </div>
        `;
    }

    _attachEventListeners() {
        const triggers = this.shadowRoot.querySelectorAll('[data-faq-trigger]');
        triggers.forEach(trigger => {
            trigger.addEventListener('click', (e) => this._handleToggle(e));
        });
    }

    _handleToggle(e) {
        const trigger = e.currentTarget;
        const index = parseInt(trigger.getAttribute('data-faq-trigger'), 10);

        if (this._expandedItems.has(index)) {
            this._expandedItems.delete(index);
        } else {
            this._expandedItems.add(index);
        }

        this._render();
    }

    _renderAnswer(answer) {
        if (!answer) return '';
        return formatTextWithLineBreaks(translateMe(answer));
    }

    _escapeHTML(text) {
        if (typeof text !== 'string') return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

if (!customElements.get('faq-accordion')) {
    customElements.define('faq-accordion', FaqAccordion);
}

export { FaqAccordion };